// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import { getLogs, subscribeToLogs, clearLogs } from '../utils/logger';
import { CloseIcon, TrashIcon, CheckmarkIcon } from './Icons';
import { sendTestPush, fetchServerLogs, fetchSystemResources } from '../api';
import { StatsManager } from '../utils/StatsManager';
import { getCurrentToken } from '../utils/notificationUtils';

export const DebugLogViewer = ({ 
    isOpen, 
    onClose,
    onRefreshPush,
    currentUser
}: { 
    isOpen: boolean, 
    onClose: () => void,
    onRefreshPush?: () => void,
    currentUser?: any
}) => {
    const [viewMode, setViewMode] = useState<'client' | 'server' | 'task_manager'>('client');
    const [logs, setLogs] = useState(getLogs());
    const [serverLogs, setServerLogs] = useState<any[]>([]);
    const [serverStats, setServerStats] = useState<any>(null);
    const [clientStats, setClientStats] = useState<any>(null);
    const [filter, setFilter] = useState('');
    const [deviceToken, setDeviceToken] = useState<string | null>(null);
    const scrollRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (isOpen) {
            if (viewMode === 'client') {
                const unsubscribe = subscribeToLogs(setLogs);
                setLogs(getLogs());
                
                getCurrentToken().then(token => {
                    if (token) setDeviceToken(token);
                });

                return unsubscribe;
            
            } else if (viewMode === 'task_manager') {
                setClientStats(StatsManager.getStats());
                const fetchStats = async () => {
                    try {
                        const data = await fetchSystemResources();
                        if (data && data.cpu) { setServerStats(data); } else { console.error('Invalid server stats:', data); setServerStats(null); }
                    } catch(e) {
                        console.error('Failed to fetch system resources', e);
                    }
                };
                fetchStats();
                const intv = setInterval(() => {
                    setClientStats(StatsManager.getStats());
                    fetchStats();
                }, 3000);
                return () => clearInterval(intv);
            } else {

                const fetchLogs = async () => {
                    try {
                        const data = await fetchServerLogs();
                        if (Array.isArray(data)) {
                            setServerLogs(data.reverse()); // Show latest first
                        } else {
                            console.warn('[System] Server logs returned non-array:', data);
                            setServerLogs([{ timestamp: new Date().toISOString(), level: 'WARN', message: 'Server returned non-array logs' }]);
                        }
                    } catch (e: any) {
                        console.error('Failed to fetch server logs:', e.message || e);
                    }
                };
                fetchLogs();
                const interval = setInterval(fetchLogs, 3000);
                return () => clearInterval(interval);
            }
        }
    }, [isOpen, viewMode]);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = 0; // Latest logs are at top
        }
    }, [logs, serverLogs]);

    if (!isOpen) return null;

    const activeLogs = viewMode === 'client' ? logs : serverLogs;

    const filteredLogs = activeLogs.filter(log => {
        const msg = (log.message || '').toLowerCase();
        const lvl = (log.level || '').toLowerCase();
        const search = filter.toLowerCase();
        return msg.includes(search) || lvl.includes(search);
    });

    return (
        <div className="fixed inset-0 z-[999] flex items-center justify-center p-4 md:p-8">
            <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={onClose} />
            <div className="relative w-full max-w-2xl h-[80vh] bg-gray-900 border border-white/10 rounded-[2rem] flex flex-col overflow-hidden shadow-2xl">
                <div className="p-6 border-b border-white/5 flex items-center justify-between bg-black/20">
                    <div className="flex items-center gap-3">
                        <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                        <h3 className="font-black italic uppercase tracking-tighter text-white">System Console</h3>
                        <div className="flex bg-white/5 p-0.5 rounded-lg border border-white/10 ml-2">
                            <button 
                                onClick={() => setViewMode('client')}
                                className={`px-2 py-0.5 rounded-md text-[9px] font-bold uppercase transition-all ${viewMode === 'client' ? 'bg-white/10 text-white shadow-lg' : 'text-white/30 hover:text-white/60'}`}
                            >
                                Client
                            </button>
                            <button 
                                onClick={() => setViewMode('server')}
                                className={`px-2 py-0.5 rounded-md text-[9px] font-bold uppercase transition-all ${viewMode === 'server' ? 'bg-white/10 text-white shadow-lg' : 'text-white/30 hover:text-white/60'}`}
                            >
                                Server
                            </button>
                            <button 
                                onClick={() => setViewMode('task_manager')}
                                className={`px-2 py-0.5 rounded-md text-[9px] font-bold uppercase transition-all ${viewMode === 'task_manager' ? 'bg-white/10 text-white shadow-lg' : 'text-white/30 hover:text-white/60'}`}
                            >
                                Task Manager
                            </button>
                        </div>
                        <span className="text-[10px] bg-white/10 px-2 py-0.5 rounded-full text-white/40">
                            {viewMode === 'client' ? logs.length : serverLogs.length} entries
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        {onRefreshPush && (
                            <button 
                                onClick={onRefreshPush} 
                                className="p-2 hover:bg-white/10 rounded-full text-blue-400 hover:text-blue-300 transition-all flex items-center gap-1 px-3"
                                title="Retry Push Setup"
                            >
                                <span className="text-[10px] font-bold uppercase">Setup Push</span>
                            </button>
                        )}
                        {currentUser && (
                            <button 
                                onClick={async () => {
                                    const username = typeof currentUser === 'string' ? currentUser : currentUser.username;
                                    console.log(`[Push] Requesting test notification for ${username}...`);
                                    try {
                                        const res = await sendTestPush(username);
                                        console.log('[Push] Server Response:', JSON.stringify(res, null, 2));
                                    } catch (e: any) {
                                        console.error('[Push] Test push request failed:', e.message || e);
                                    }
                                }} 
                                className="p-2 hover:bg-white/10 rounded-full text-green-400 hover:text-green-300 transition-all flex items-center gap-1 px-3"
                                title="Send Test Push"
                            >
                                <span className="text-[10px] font-bold uppercase">Test Push</span>
                            </button>
                        )}
                        <button onClick={clearLogs} className="p-2 hover:bg-white/10 rounded-full text-white/40 hover:text-red-400 transition-all">
                            <TrashIcon className="w-5 h-5" />
                        </button>
                        <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full text-white">
                            <CloseIcon className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                
                {viewMode === 'task_manager' ? (
                    <div className="flex-1 p-4 overflow-y-auto font-mono text-[10px] space-y-6">
                        {/* Server Stats */}
                        <div className="space-y-3">
                            <h4 className="text-white font-bold uppercase tracking-widest border-b border-white/10 pb-1 flex items-center gap-2">
                                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                                Backend Server Resources
                            </h4>
                            {serverStats && serverStats.cpu ? (
                                <div className="grid grid-cols-2 gap-2 text-white/70">
                                    <div className="bg-black/30 p-2 rounded border border-white/5">
                                        <div className="text-white/40 uppercase mb-1">CPU Load (1m, 5m, 15m)</div>
                                        <div className="text-white font-bold">{serverStats.cpu.loadAvg.map((n: any) => n.toFixed(2)).join(' / ')}</div>
                                    </div>
                                    <div className="bg-black/30 p-2 rounded border border-white/5">
                                        <div className="text-white/40 uppercase mb-1">CPU Cores</div>
                                        <div className="text-white font-bold">{serverStats.cpu.cores} Cores</div>
                                    </div>
                                    <div className="bg-black/30 p-2 rounded border border-white/5">
                                        <div className="text-white/40 uppercase mb-1">Node JS Heap</div>
                                        <div className="text-white font-bold">{(serverStats.memory.process.heapUsed / 1024 / 1024).toFixed(1)} MB / {(serverStats.memory.process.heapTotal / 1024 / 1024).toFixed(1)} MB</div>
                                    </div>
                                    <div className="bg-black/30 p-2 rounded border border-white/5">
                                        <div className="text-white/40 uppercase mb-1">OS Memory (Free / Total)</div>
                                        <div className="text-white font-bold">{(serverStats.memory.free / 1024 / 1024 / 1024).toFixed(1)} GB / {(serverStats.memory.total / 1024 / 1024 / 1024).toFixed(1)} GB</div>
                                    </div>
                                </div>
                            ) : (
                                <div className="text-white/30 animate-pulse">Fetching server stats...</div>
                            )}
                        </div>
                        
                        {/* Client Stats */}
                        <div className="space-y-3">
                            <h4 className="text-white font-bold uppercase tracking-widest border-b border-white/10 pb-1 flex items-center gap-2">
                                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                                Client App Process
                            </h4>
                            {clientStats ? (
                                <div className="space-y-4 text-white/70">
                                    {clientStats.current && (
                                        <div>
                                            
                                            <div className="text-white/40 uppercase mb-2">Client Memory Allocation</div>
                                            <div className="grid grid-cols-2 gap-2 mb-4">
                                                {(() => {
                                                    const snaps = clientStats.current.memorySnapshots || [];
                                                    const latest = snaps[snaps.length - 1];
                                                    if (!latest) return <div className="text-white/30 text-xs">No memory data available (Requires Chromium based browser)</div>;
                                                    return (
                                                        <>
                                                            <div className="bg-black/30 p-2 rounded border border-white/5 flex flex-col">
                                                                <span className="uppercase text-[9px] text-white/50 mb-1">Active JS Heap</span>
                                                                <span className="text-white font-bold">{(latest.usedJSHeapSize / 1024 / 1024).toFixed(1)} MB</span>
                                                            </div>
                                                            <div className="bg-black/30 p-2 rounded border border-white/5 flex flex-col">
                                                                <span className="uppercase text-[9px] text-white/50 mb-1">Total JS Heap</span>
                                                                <span className="text-white font-bold">{(latest.totalJSHeapSize / 1024 / 1024).toFixed(1)} MB</span>
                                                            </div>
                                                        </>
                                                    );
                                                })()}
                                            </div>
                                            <div className="text-white/40 uppercase mb-2">Current Session Usage</div>

                                            <div className="grid grid-cols-2 gap-2">
                                                {Object.entries(clientStats.current.activeTimePerMod || {})
                                                    .sort((a: any, b: any) => b[1] - a[1])
                                                    .map(([mod, time]: any) => (
                                                    <div key={mod} className="bg-black/30 p-2 rounded border border-white/5 flex justify-between items-center">
                                                        <span className="uppercase text-[9px]">{mod}</span>
                                                        <span className="text-white font-bold">{(time / 1000).toFixed(1)}s</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {clientStats.previous && (
                                        <div>
                                            <div className="text-white/40 uppercase mb-2">Previous Session Usage</div>
                                            <div className="grid grid-cols-2 gap-2 opacity-70">
                                                {Object.entries(clientStats.previous.activeTimePerMod || {})
                                                    .sort((a: any, b: any) => b[1] - a[1])
                                                    .map(([mod, time]: any) => (
                                                    <div key={mod} className="bg-black/30 p-2 rounded border border-white/5 flex justify-between items-center">
                                                        <span className="uppercase text-[9px]">{mod}</span>
                                                        <span className="text-white font-bold">{(time / 1000).toFixed(1)}s</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="text-white/30">Loading client stats...</div>
                            )}
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="p-4 border-b border-white/5 bg-black/10">
                    <input 
                        type="text" 
                        placeholder="Filter logs (e.g. [Push], Error, token)..."
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-xs text-white outline-none focus:border-blue-500 transition-all"
                    />
                    {deviceToken && (
                        <div className="mt-2 flex items-center justify-between text-[9px] text-white/30 uppercase tracking-widest font-mono">
                            <span className="truncate mr-2">Device Token: {deviceToken.substring(0, 15)}...{deviceToken.substring(deviceToken.length - 10)}</span>
                            <button 
                                onClick={() => {
                                    navigator.clipboard.writeText(deviceToken);
                                    console.log('[System] Token copied to clipboard');
                                }}
                                className="hover:text-white transition-colors flex-shrink-0"
                            >
                                Copy Token
                            </button>
                        </div>
                    )}
                </div>

                <div 
                    ref={scrollRef} 
                    className="flex-1 overflow-y-auto p-4 space-y-1 font-mono text-[10px] no-scrollbar"
                    style={{ touchAction: 'pan-y', WebkitOverflowScrolling: 'touch' }}
                    onTouchStart={(e) => e.stopPropagation()}
                    onTouchMove={(e) => e.stopPropagation()}
                    onTouchEnd={(e) => e.stopPropagation()}
                >
                    {filteredLogs.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full opacity-20 italic">
                            <p>No {viewMode} logs matching filter</p>
                        </div>
                    ) : (
                        filteredLogs.map((log, i) => (
                            <div key={i} className={`p-2 rounded border border-white/5 break-words whitespace-pre-wrap ${
                                log.level.toLowerCase() === 'error' ? 'bg-red-500/10 border-red-500/20 text-red-400' :
                                log.level.toLowerCase() === 'warn' ? 'bg-orange-500/10 border-orange-500/20 text-orange-400' :
                                'text-gray-300'
                            }`}>
                                <div className="flex items-center justify-between mb-1 opacity-40">
                                    <span className="uppercase font-black tracking-widest">{log.level}</span>
                                    <span>{log.timestamp ? new Date(log.timestamp).toLocaleTimeString() : ''}</span>
                                </div>
                                <div className="font-medium">{log.message}</div>
                            </div>
                        ))
                    )}
                </div>
                    </>
                )}
                
                <div className="p-4 bg-black/40 text-[9px] text-white/20 text-center uppercase tracking-widest font-black italic">
                    Internal Debug Engine v1.0
                </div>
            </div>
        </div>
    );
};
